import { useEffect, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { useFileUpload } from "@/lib/uploadProgress";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";

const emptyBundle = () => ({
  title: "",
  description: "",
  image: "",
  products: [],
  services: [{ name: "Sab Saaf Kare", per_kg: 10 }],
  is_active: true,
});

export default function AdminAbp() {
  const uploadFile = useFileUpload();
  const [settings, setSettings] = useState({ section_title: "Product Mixture & More" });
  const [bundles, setBundles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/merchant/abp").then(({ data }) => {
      setSettings(data.settings || { section_title: "Product Mixture & More" });
      setBundles(data.bundles || []);
    }).catch((e) => toast.error(extractErr(e)));
    api.get("/merchant/products").then(({ data }) => {
      const rows = Array.isArray(data) ? data : (data.items || data.products || []);
      setCatalog(rows.filter((p) => p && p.id));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/merchant/abp/settings", settings);
      setSettings(data);
      toast.success("Section title saved");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "image");
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      setDraft((d) => ({ ...d, image: data.url || data.file_url || data.path || "" }));
    } catch (e) { toast.error(extractErr(e)); }
  };

  const saveBundle = async () => {
    setSaving(true);
    try {
      if (draft.id) await api.put(`/merchant/abp/bundles/${draft.id}`, draft);
      else await api.post("/merchant/abp/bundles", draft);
      toast.success("Bundle saved");
      setDraft(null);
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bundle?")) return;
    try {
      await api.delete(`/merchant/abp/bundles/${id}`);
      toast.success("Bundle deleted");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const toggleProduct = (product) => {
    setDraft((d) => {
      const has = (d.products || []).some((p) => p.product_id === product.id);
      const products = has
        ? d.products.filter((p) => p.product_id !== product.id)
        : [...d.products, { product_id: product.id, service_name: "", service_per_kg: 0 }];
      return { ...d, products };
    });
  };

  return (
    <div className="max-w-4xl space-y-4" data-testid="admin-abp">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">ABP Capabilities</h1>
        <p className="text-sm text-slate-500 mt-1">Kilogram mixtures for every theme. 1 quantity = 1 KG. Shoppers add as much as is in stock. A ticked service priced at ₹10 is charged as ₹10 × the KG they added.</p>
      </div>

      <Card className="p-5 space-y-3">
        <Label htmlFor="abp-section-title">Storefront section title</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input id="abp-section-title" data-testid="abp-section-title" value={settings.section_title || ""} onChange={(e) => setSettings({ section_title: e.target.value })} />
          <Button type="button" onClick={saveSettings} data-testid="abp-save-title">Save title</Button>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Bundles</h2>
        <Button type="button" data-testid="abp-new-bundle" onClick={() => setDraft(emptyBundle())}><Plus className="w-4 h-4 mr-1" /> New bundle</Button>
      </div>

      {draft && (
        <Card className="p-5 space-y-4" data-testid="abp-bundle-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input data-testid="abp-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Aata & Tel" />
            </div>
            <div>
              <Label>Photo</Label>
              <div className="flex items-center gap-2">
                {draft.image ? <img src={mediaUrl(draft.image)} alt="" className="w-12 h-12 rounded-full object-cover" /> : null}
                <label className="text-sm font-semibold text-blue-700 cursor-pointer">
                  Upload
                  <input type="file" hidden accept="image/*" data-testid="abp-image" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                </label>
              </div>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <textarea className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" rows={3} data-testid="abp-description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800 mb-2">Products in this bundle</div>
            <div className="max-h-64 overflow-auto border rounded-lg divide-y">
              {catalog.map((p) => {
                const row = (draft.products || []).find((x) => x.product_id === p.id);
                return (
                  <div key={p.id} className="p-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={Boolean(row)} data-testid={`abp-pick-${p.id}`} onChange={() => toggleProduct(p)} />
                      <span className="font-medium">{p.name}</span>
                      <span className="text-slate-400">₹{p.price}/KG</span>
                    </label>
                    {row ? (
                      <div className="mt-2 grid sm:grid-cols-2 gap-2 pl-6">
                        <Input placeholder="Product service name (optional)" value={row.service_name || ""} onChange={(e) => setDraft({ ...draft, products: draft.products.map((x) => x.product_id === p.id ? { ...x, service_name: e.target.value } : x) })} />
                        <Input type="number" min={0} placeholder="₹ per KG" value={row.service_per_kg || ""} onChange={(e) => setDraft({ ...draft, products: draft.products.map((x) => x.product_id === p.id ? { ...x, service_per_kg: Number(e.target.value) } : x) })} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {catalog.length === 0 ? <div className="p-3 text-sm text-slate-400">Add products in the catalog first.</div> : null}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800">Custom service charge for the whole mix</div>
              <Button type="button" variant="outline" size="sm" onClick={() => setDraft({ ...draft, services: [...(draft.services || []), { name: "", per_kg: 0 }] })}>Add charge</Button>
            </div>
            <p className="text-xs text-slate-500 mb-2">Example: name Sab Saaf Kare and price ₹10. The shopper ticks it. On a 10 KG mix that adds ₹100. Unticked services are not charged.</p>
            {(draft.services || []).map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2 mb-2">
                <Input data-testid={`abp-service-name-${i}`} placeholder="Sab Saaf Kare" value={s.name} onChange={(e) => setDraft({ ...draft, services: draft.services.map((x, k) => k === i ? { ...x, name: e.target.value } : x) })} />
                <Input type="number" min={0} data-testid={`abp-service-price-${i}`} placeholder="10" value={s.per_kg} onChange={(e) => setDraft({ ...draft, services: draft.services.map((x, k) => k === i ? { ...x, per_kg: Number(e.target.value) } : x) })} />
                <Button type="button" variant="ghost" onClick={() => setDraft({ ...draft, services: draft.services.filter((_, k) => k !== i) })}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={draft.is_active !== false} onCheckedChange={(v) => setDraft({ ...draft, is_active: v })} />
            <span className="text-sm">Visible on the storefront</span>
          </div>
          <div className="flex gap-2">
            <Button type="button" data-testid="abp-save-bundle" disabled={saving} onClick={saveBundle}>{saving ? "Saving…" : "Save bundle"}</Button>
            <Button type="button" variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {bundles.map((b) => (
          <Card key={b.id} className="p-4 flex items-center gap-3" data-testid={`abp-row-${b.id}`}>
            <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 shrink-0">
              {b.image ? <img src={mediaUrl(b.image)} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{b.title}</div>
              <div className="text-xs text-slate-500">{(b.products || []).length} products · {(b.services || []).map((s) => `${s.name} ₹${s.per_kg}/KG`).join(", ") || "No service charge"}</div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setDraft({ ...emptyBundle(), ...b, products: b.products || [], services: b.services || [] })}>Edit</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(b.id)}><Trash2 className="w-4 h-4" /></Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
